import { cn } from "../../utils/cn";
import { useFitLabel } from "./useFitLabel";

type FitLabelProps = {
  /** The label text; also the re-fit trigger (locale change swaps it). */
  text: string;
  /** Extra classes (color/weight/tracking). Layout classes are supplied here. */
  className?: string;
  /** Design-token size in px this label starts from before stepping down (e.g. 14 = text-sm, 12 = text-xs). */
  maxPx?: number;
};

/**
 * FitLabel — a nav label that sizes itself to the width it has, and can NEVER clip.
 *
 * Fills its flex row (`flex-1 min-w-0`) so its box is the true available width,
 * then {@link useFitLabel} picks the first step (font-size down to the floor,
 * then floor + `tracking-tight`, then floor + `tracking-tighter`) that keeps the
 * text on one line.
 *
 * It renders `whitespace-nowrap` ONLY once a measurement has CONFIRMED a
 * single-line fit (`nowrap === true`). Until then — the initial/unfitted frame,
 * or a below-floor result — it renders WRAPPED (`whitespace-normal break-words
 * hyphens-auto`). There is intentionally no `overflow-hidden`: a wrong or
 * not-yet-applied measurement can only ever wrap the text, never hide it. So a
 * label is always either single-line-and-fully-visible or wrapped — never
 * clipped, never horizontally scrolling.
 *
 * line-height is inherited (as a length) from the row's text-size class, so a
 * stepped-down font keeps the row height stable — density is preserved.
 */
export const FitLabel = ({ text, className, maxPx = 14 }: FitLabelProps) => {
  const { ref, fontSize, tracking, nowrap } = useFitLabel(text, maxPx);
  return (
    <span
      ref={ref}
      style={{ fontSize: `${fontSize}px` }}
      className={cn(
        // text-left: a block flex-1 span otherwise inherits a row's text-align
        // (a <button> defaults to center), which centered the Account group labels.
        "block min-w-0 flex-1 text-left",
        tracking === "tight" && "tracking-tight",
        tracking === "tighter" && "tracking-tighter",
        nowrap ? "whitespace-nowrap" : "whitespace-normal break-words hyphens-auto",
        className
      )}
    >
      {text}
    </span>
  );
};
